import Dexie, { type EntityTable } from "dexie";
import { parseTheme, type Theme } from "@/model/theme";

export interface PresetRow {
  id: string;
  name: string;
  theme: Theme;
  favorite: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SnapshotRow {
  id?: number;
  presetId: string;
  label: string;
  /** Auto snapshots are taken on every save and pruned; named ones are kept. */
  auto: boolean;
  theme: Theme;
  createdAt: string;
}

interface KvRow {
  key: string;
  value: unknown;
}

const MAX_AUTO_SNAPSHOTS = 30;

export class ThemesDb extends Dexie {
  presets!: EntityTable<PresetRow, "id">;
  snapshots!: EntityTable<SnapshotRow, "id">;
  kv!: EntityTable<KvRow, "key">;

  // The database keeps the app's first name: renaming it would orphan saved presets.
  constructor(name = "hue") {
    super(name);
    this.version(1).stores({
      presets: "id, name, updatedAt, favorite",
      snapshots: "++id, presetId, createdAt",
      kv: "key",
    });
  }
}

export const db = new ThemesDb();

export async function savePreset(theme: Theme, database = db): Promise<PresetRow> {
  const now = new Date().toISOString();
  return database.transaction("rw", database.presets, database.snapshots, async () => {
    const existing = await database.presets.get(theme.id);
    const row: PresetRow = {
      id: theme.id,
      name: theme.name,
      theme,
      favorite: existing?.favorite ?? false,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    };
    await database.presets.put(row);
    await database.snapshots.add({
      presetId: theme.id,
      label: "Saved",
      auto: true,
      theme,
      createdAt: now,
    });
    const autos = await database.snapshots
      .where("presetId")
      .equals(theme.id)
      .filter((s) => s.auto)
      .sortBy("createdAt");
    const excess = autos.length - MAX_AUTO_SNAPSHOTS;
    if (excess > 0)
      await database.snapshots.bulkDelete(autos.slice(0, excess).map((s) => s.id ?? -1));
    return row;
  });
}

export async function addSnapshot(theme: Theme, label: string, database = db): Promise<void> {
  await database.snapshots.add({
    presetId: theme.id,
    label,
    auto: false,
    theme,
    createdAt: new Date().toISOString(),
  });
}

export async function deletePreset(id: string, database = db): Promise<void> {
  await database.transaction("rw", database.presets, database.snapshots, async () => {
    await database.presets.delete(id);
    await database.snapshots.where("presetId").equals(id).delete();
  });
}

export async function setFavorite(id: string, favorite: boolean, database = db): Promise<void> {
  await database.presets.update(id, { favorite });
}

export async function renamePreset(id: string, name: string, database = db): Promise<void> {
  const row = await database.presets.get(id);
  if (!row) return;
  await database.presets.put({ ...row, name, theme: { ...row.theme, name } });
}

export async function getKv<T>(key: string, database = db): Promise<T | undefined> {
  return (await database.kv.get(key))?.value as T | undefined;
}

export async function setKv(key: string, value: unknown, database = db): Promise<void> {
  await database.kv.put({ key, value });
}

/** Backups written before the app was renamed say "hue-backup"; both import. */
export const BACKUP_KINDS = ["themes-backup", "hue-backup"] as const;

export interface Backup {
  kind: (typeof BACKUP_KINDS)[number];
  version: 1;
  exportedAt: string;
  presets: PresetRow[];
  snapshots: SnapshotRow[];
}

export async function exportBackup(database = db): Promise<Backup> {
  return {
    kind: "themes-backup",
    version: 1,
    exportedAt: new Date().toISOString(),
    presets: await database.presets.toArray(),
    snapshots: (await database.snapshots.toArray()).map(({ id: _id, ...s }) => s),
  };
}

/** Merges a backup: presets with the same id are replaced, snapshots are appended. */
export async function importBackup(data: unknown, database = db): Promise<number> {
  const b = data as Partial<Backup>;
  if (!BACKUP_KINDS.includes(b?.kind as Backup["kind"]) || !Array.isArray(b.presets)) {
    throw new Error("Not a themes backup file.");
  }
  const presets = b.presets.map((p) => {
    const theme = parseTheme(p.theme);
    return { ...p, id: theme.id, name: theme.name, theme, favorite: Boolean(p.favorite) };
  });
  const snapshots = (b.snapshots ?? []).map(({ id: _id, ...s }) => ({
    ...s,
    theme: parseTheme(s.theme),
  }));
  await database.transaction("rw", database.presets, database.snapshots, async () => {
    await database.presets.bulkPut(presets);
    await database.snapshots.bulkAdd(snapshots);
  });
  return presets.length;
}
